<script lang="ts" setup>
export interface Props {
  prices: {
    finalPrice?: string | number
    regularPrice: string | number
  }
  forceFullPrice?: boolean
}

const props = defineProps<Props>()

const showDiscount = computed(() => {
  return props.prices.regularPrice && parseFloat(props.prices.regularPrice) > parseFloat(props.prices.finalPrice)
})

const regularPrice = computed(() => {
  return Number(props.prices?.regularPrice || 0)
})

const finalPrice = computed(() => {
  return Number(props.prices?.finalPrice || 0)
})

const formatRegularPrice = computed<Intl.NumberFormatOptions>(() => {
  return {
    style: 'currency',
    currency: 'EUR',
    useGrouping: false,
    ...(Number.isInteger(props.prices.regularPrice) && !props.forceFullPrice ? { maximumFractionDigits: 0 } : {}),
  }
})

const formatFinalPrice = computed<Intl.NumberFormatOptions>(() => {
  return {
    style: 'currency',
    currency: 'EUR',
    useGrouping: false,
    ...(Number.isInteger(props.prices.finalPrice) && !props.forceFullPrice ? { maximumFractionDigits: 0 } : {}),
  }
})
</script>

<template>
  <div
    class="price"
    :class="{
      'price--sale': showDiscount,
    }"
  >
    <slot name="prefix" />

    <i18n-n
      v-if="regularPrice"
      tag="span"
      :value="regularPrice"
      :format="formatRegularPrice"
      class="price__regular-price"
      :class="{ 'price--rounded': regularPrice === Math.round(regularPrice) && formatRegularPrice.maximumFractionDigits === 0 }"
    >
      <template #currency="slotProps">
        <span class="price__currency">{{ slotProps.currency }}</span>
      </template>
      <template #integer="slotProps">
        <span class="price__integer">{{ slotProps.integer }}</span>
      </template>
      <template #decimal="slotProps">
        <span class="price__decimal">{{ slotProps.decimal }}</span>
      </template>
      <template #fraction="slotProps">
        <span class="price__fraction">{{ slotProps.fraction }}</span>
      </template>
      <template #literal="slotProps">
        <span class="price__literal">{{ slotProps.literal }}</span>
      </template>
    </i18n-n>

    <i18n-n
      v-if="showDiscount && finalPrice"
      tag="span"
      :value="finalPrice"
      :format="formatFinalPrice"
      class="price__final-price"
      :class="{ 'price--rounded': finalPrice === Math.round(finalPrice) && formatFinalPrice.maximumFractionDigits === 0 }"
    >
      <template #currency="slotProps">
        <span class="price__currency">{{ slotProps.currency }}</span>
      </template>
      <template #integer="slotProps">
        <span class="price__integer">{{ slotProps.integer }}</span>
      </template>
      <template #decimal="slotProps">
        <span class="price__decimal">{{ slotProps.decimal }}</span>
      </template>
      <template #fraction="slotProps">
        <span class="price__fraction">{{ slotProps.fraction }}</span>
      </template>
    </i18n-n>

    <slot name="suffix" />
  </div>
</template>

<style>
@import url("~layers/components/Ui/Price.css");
</style>
